import { useState, useEffect, useCallback, useMemo } from 'react';
import { useMarketContext } from '../context/MarketDataContext';
import { getValidToken } from '../utils/auth';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const API_URL = process.env.REACT_APP_API_URL || '';

const SECTOR_COLORS = {
  'Information Technology': '#8b5cf6',
  'Health Care': '#06b6d4',
  'Financials': '#f59e0b',
  'Consumer Discretionary': '#ec4899',
  'Communication Services': '#3b82f6',
  'Industrials': '#6b7280',
  'Consumer Staples': '#10b981',
  'Energy': '#ef4444',
  'Utilities': '#a78bfa',
  'Real Estate': '#f97316',
  'Materials': '#14b8a6',
};

function getScoreColor(score) {
  if (score > 30) return '#22c55e';
  if (score > 10) return '#86efac';
  if (score > -10) return '#9ca3af';
  if (score > -30) return '#fca5a5';
  return '#ef4444';
}

function getScoreLabel(score) {
  if (score > 30) return 'Strong Bullish';
  if (score > 10) return 'Bullish';
  if (score > -10) return 'Neutral';
  if (score > -30) return 'Bearish';
  return 'Strong Bearish';
}

function formatChartTime(iso) {
  const d = new Date(iso);
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function ScoreCard({ title, score, matchedCount }) {
  const color = getScoreColor(score);
  const label = getScoreLabel(score);
  return (
    <div className="bg-gray-900 rounded-2xl p-6 text-center flex flex-col items-center justify-center">
      <p className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-3">{title}</p>
      <div className="relative mb-3">
        <div className="text-6xl font-bold tabular-nums" style={{ color }}>
          {matchedCount > 0 ? (score >= 0 ? '+' : '') + score.toFixed(1) : '\u2014'}
        </div>
      </div>
      <div
        className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm font-semibold"
        style={{ backgroundColor: color + '20', color }}
      >
        <span className="inline-block w-2 h-2 rounded-full" style={{ backgroundColor: color }} />
        {matchedCount > 0 ? label : 'Waiting for data'}
      </div>
      <div className="w-full mt-5">
        <div className="relative h-2 rounded-full bg-gray-800 overflow-hidden">
          <div
            className="absolute top-0 h-full rounded-full transition-all duration-500"
            style={{
              backgroundColor: color,
              left: `${Math.min(50, 50 + (score / 2))}%`,
              width: `${Math.abs(score / 2)}%`,
            }}
          />
          <div className="absolute top-0 left-1/2 w-0.5 h-full bg-gray-600" />
        </div>
        <div className="flex justify-between mt-1 text-[10px] text-gray-500">
          <span>-100</span>
          <span>0</span>
          <span>+100</span>
        </div>
      </div>
    </div>
  );
}

function ScoreChart({ title, data, dataKey, gradientId }) {
  const lastScore = data.length > 0 ? (data[data.length - 1][dataKey] ?? 0) : 0;
  const color = getScoreColor(lastScore);
  return (
    <div className="lg:col-span-2 bg-gray-900 rounded-2xl p-5">
      <p className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-3">{title}</p>
      {data.length < 2 ? (
        <div className="h-40 flex items-center justify-center text-gray-500 text-sm">
          Collecting data points...
        </div>
      ) : (
        <div className="h-40">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={data}>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
              <XAxis dataKey="time" tick={{ fontSize: 10, fill: '#9ca3af' }} interval="preserveStartEnd" />
              <YAxis tick={{ fontSize: 10, fill: '#9ca3af' }} domain={['auto', 'auto']} />
              <Tooltip
                contentStyle={{ backgroundColor: '#1f2937', border: '1px solid #374151', borderRadius: '8px' }}
                labelStyle={{ color: '#9ca3af' }}
                formatter={(v) => [v != null ? v.toFixed(2) : '\u2014', 'Score']}
              />
              <defs>
                <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={color} stopOpacity={0.3} />
                  <stop offset="100%" stopColor={color} stopOpacity={0} />
                </linearGradient>
              </defs>
              <Area
                type="monotone"
                dataKey={dataKey}
                stroke={color}
                fill={`url(#${gradientId})`}
                strokeWidth={2}
                dot={false}
                connectNulls
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}

function Spy0dteWidget() {
  const [contracts, setContracts] = useState(null);
  const [sort, setSort] = useState({ col: 'volume', dir: 'desc' });
  const [connStatus, setConnStatus] = useState('connecting');
  const [updatedAt, setUpdatedAt] = useState(null);
  const [, setTick] = useState(0);

  // Re-render every 5s so "Updated Xs ago" stays fresh.
  useEffect(() => {
    const t = setInterval(() => setTick(n => n + 1), 5000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    let abortCtrl = null;
    let reconnectTimer = null;
    let pollTimer = null;
    let mounted = true;
    let sseDelay = 1000;

    async function pollOnce() {
      try {
        const token = await getValidToken();
        const res = await fetch(`${API_URL}/api/spy-0dte`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (mounted) { setContracts(data); setUpdatedAt(Date.now()); setConnStatus('live'); }
      } catch { if (mounted) setConnStatus('reconnecting'); }
    }

    function startPoll() {
      pollOnce();
      pollTimer = setInterval(pollOnce, 5000);
    }

    async function connectSSE() {
      if (!mounted) return;
      setConnStatus('connecting');
      try {
        const token = await getValidToken();
        abortCtrl = new AbortController();
        const res = await fetch(`${API_URL}/sse/spy-0dte`, {
          headers: { Authorization: `Bearer ${token}` },
          signal: abortCtrl.signal,
        });
        if (!res.ok) throw new Error(`SSE ${res.status}`);

        const reader = res.body.getReader();
        const dec = new TextDecoder();
        let buf = '';
        if (mounted) { setConnStatus('live'); sseDelay = 1000; }

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          buf += dec.decode(value, { stream: true });
          const lines = buf.split('\n');
          buf = lines.pop();
          for (const line of lines) {
            if (line.startsWith('data: ')) {
              try {
                const data = JSON.parse(line.slice(6));
                if (mounted) { setContracts(data); setUpdatedAt(Date.now()); }
              } catch { /* ignore malformed frame */ }
            }
          }
        }
      } catch (e) {
        if (!mounted || e.name === 'AbortError') return;
        if (mounted) setConnStatus('reconnecting');
        if (sseDelay <= 8000) {
          reconnectTimer = setTimeout(() => { sseDelay = Math.min(sseDelay * 2, 30000); connectSSE(); }, sseDelay);
        } else {
          startPoll(); // give up on SSE after repeated failures, fall back to polling
        }
      }
    }

    connectSSE();
    return () => {
      mounted = false;
      if (abortCtrl) abortCtrl.abort();
      clearTimeout(reconnectTimer);
      clearInterval(pollTimer);
    };
  }, []);

  const toggleSort = (col) =>
    setSort(s => s.col === col ? { col, dir: s.dir === 'desc' ? 'asc' : 'desc' } : { col, dir: 'desc' });

  const sorted = useMemo(() => {
    if (!contracts) return null;
    const cols = { volume: 'volume', price: 'price', change_pct: 'change_pct', strike: 'strike' };
    const key = cols[sort.col] || 'volume';
    return [...contracts].sort((a, b) =>
      sort.dir === 'desc' ? b[key] - a[key] : a[key] - b[key]
    );
  }, [contracts, sort]);

  const ago = (() => {
    if (!updatedAt) return null;
    const s = Math.floor((Date.now() - updatedAt) / 1000);
    if (s < 5) return 'just now';
    if (s < 60) return `${s}s ago`;
    return `${Math.floor(s / 60)}m ago`;
  })();

  const dotColor = connStatus === 'live' ? '#22c55e' : connStatus === 'reconnecting' ? '#f59e0b' : '#6b7280';
  const dotLabel = connStatus === 'live' ? 'Live' : connStatus === 'reconnecting' ? 'Reconnecting…' : 'Connecting…';

  const SortArrow = ({ col }) =>
    sort.col === col
      ? <span className="ml-1" style={{ color: '#60a5fa' }}>{sort.dir === 'desc' ? '↓' : '↑'}</span>
      : <span className="ml-1 text-gray-600">↕</span>;

  return (
    <div className="bg-gray-900 rounded-2xl p-5 mb-5">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <p className="text-xs font-medium text-gray-400 uppercase tracking-wider">SPY 0 DTE Options</p>
          {ago && <span className="text-xs text-gray-500">Updated {ago}</span>}
        </div>
        <div className="flex items-center gap-2">
          <span className="inline-block w-2 h-2 rounded-full" style={{ backgroundColor: dotColor }} />
          <span className="text-xs text-gray-400">{dotLabel}</span>
        </div>
      </div>

      {sorted === null && (
        <div className="space-y-2">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="h-7 bg-gray-800 rounded animate-pulse" />
          ))}
        </div>
      )}

      {sorted !== null && sorted.length === 0 && (
        <div className="text-center py-8 text-gray-500 text-sm">No 0 DTE contracts today</div>
      )}

      {sorted !== null && sorted.length > 0 && (
        <div className="overflow-auto" style={{ maxHeight: 320 }}>
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-gray-900">
              <tr>
                <th className="text-left pb-2 pr-3 text-xs font-medium text-gray-400 uppercase tracking-wider w-16">Type</th>
                <th
                  className="text-left pb-2 pr-3 text-xs font-medium text-gray-400 uppercase tracking-wider cursor-pointer select-none hover:text-gray-200"
                  onClick={() => toggleSort('strike')}
                >Contract <SortArrow col="strike" /></th>
                <th
                  className="text-right pb-2 pr-3 text-xs font-medium text-gray-400 uppercase tracking-wider cursor-pointer select-none hover:text-gray-200"
                  onClick={() => toggleSort('price')}
                >Price <SortArrow col="price" /></th>
                <th
                  className="text-right pb-2 pr-3 text-xs font-medium text-gray-400 uppercase tracking-wider cursor-pointer select-none hover:text-gray-200"
                  onClick={() => toggleSort('change_pct')}
                >Chg% <SortArrow col="change_pct" /></th>
                <th
                  className="text-right pb-2 text-xs font-medium text-gray-400 uppercase tracking-wider cursor-pointer select-none hover:text-gray-200"
                  onClick={() => toggleSort('volume')}
                >Volume <SortArrow col="volume" /></th>
              </tr>
            </thead>
            <tbody>
              {sorted.map(c => {
                const isCall = c.type === 'C';
                const chgColor = c.change_pct >= 0 ? '#22c55e' : '#ef4444';
                // Convert Alpaca OCC format (SPY260625C00740000) to TradingView OPRA format (SPY260625C740.0)
                // Last 9 chars of OCC symbol = type (1) + 8-digit strike → slice(0,-9) gives "SPY260625"
                const tvBase = c.symbol.slice(0, -9); // e.g. "SPY260625"
                const tvStrike = Number.isInteger(c.strike) ? c.strike.toFixed(1) : c.strike.toString();
                const tvLink = `https://www.tradingview.com/chart/GYtOhxAt/?symbol=OPRA%3A${tvBase}${c.type}${tvStrike}`;
                return (
                  <tr key={c.symbol} className="border-t border-gray-800 hover:bg-gray-800/40">
                    <td className="py-1.5 pr-3">
                      <span
                        className="px-1.5 py-0.5 rounded text-xs font-semibold"
                        style={{
                          backgroundColor: isCall ? 'rgba(34,197,94,0.15)' : 'rgba(239,68,68,0.15)',
                          color: isCall ? '#22c55e' : '#ef4444',
                        }}
                      >{isCall ? 'CALL' : 'PUT'}</span>
                    </td>
                    <td className="py-1.5 pr-3">
                      <a
                        href={tvLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-mono text-xs hover:text-blue-300"
                        style={{ color: '#60a5fa' }}
                      >${c.strike % 1 === 0 ? c.strike.toFixed(0) : c.strike.toFixed(1)}</a>
                    </td>
                    <td className="py-1.5 pr-3 text-right font-mono text-gray-200">${c.price.toFixed(2)}</td>
                    <td className="py-1.5 pr-3 text-right font-mono font-semibold" style={{ color: chgColor }}>
                      {c.change_pct >= 0 ? '+' : ''}{c.change_pct.toFixed(1)}%
                    </td>
                    <td className="py-1.5 text-right font-mono text-gray-300">{c.volume.toLocaleString()}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default function SP500PreMarket() {
  const { tickers } = useMarketContext();
  const [components, setComponents] = useState([]);
  const [sessionOpens, setSessionOpens] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [scoreHistory, setScoreHistory] = useState({});
  const [sortCol, setSortCol] = useState('weight');
  const [sortDir, setSortDir] = useState('desc');

  const fetchData = useCallback(async (initial) => {
    try {
      const token = await getValidToken();
      const fetches = [
        fetch(`${API_URL}/api/sp500/score-history`, { headers: { Authorization: `Bearer ${token}` } }),
      ];
      if (initial) {
        fetches.push(
          fetch(`${API_URL}/api/sp500/components`, { headers: { Authorization: `Bearer ${token}` } }),
          fetch(`${API_URL}/api/sp500/session-opens`, { headers: { Authorization: `Bearer ${token}` } }),
        );
      }
      const results = await Promise.all(fetches);

      if (results[0].ok) {
        const historyData = await results[0].json();
        const now = new Date();
        const fiveMinAgo = now.getTime() - 300000;
        const oneHourAgo = now.getTime() - 3600000;
        const etDateStr = now.toLocaleDateString('en-US', { timeZone: 'America/New_York', year: 'numeric', month: '2-digit', day: '2-digit' });
        const [em, ed, ey] = etDateStr.split('/');

        const formatted = (historyData || []).map(p => ({
          ...p,
          ts: new Date(p.time).getTime(),
          time: formatChartTime(p.time),
        }));

        const etNow = new Date(now.toLocaleString('en-US', { timeZone: 'America/New_York' }));
        const etOffset = now.getTime() - etNow.getTime();
        const pmStart = new Date(`${ey}-${em}-${ed}T04:00:00`).getTime() + etOffset;

        setScoreHistory({
          fiveMin: formatted.filter(p => p.ts > fiveMinAgo),
          hour: formatted.filter(p => p.ts > oneHourAgo),
          premarket: formatted.filter(p => p.ts >= pmStart),
        });
      }

      if (initial) {
        if (results[1] && results[1].ok) {
          setComponents(await results[1].json());
        }
        if (results[2] && results[2].ok) {
          const opensData = await results[2].json();
          const opensMap = {};
          for (const o of opensData) {
            opensMap[o.symbol] = { premarket_open: o.premarket_open, main_open: o.main_open };
          }
          setSessionOpens(opensMap);
        }
      }
    } catch (err) {
      if (initial) setError(err.message);
    } finally {
      if (initial) setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(true); }, [fetchData]);
  useEffect(() => {
    const interval = setInterval(() => fetchData(false), 30000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const getPremarketOpen = useCallback((ticker, comp) => {
    if (ticker && ticker.premarketOpen > 0) return ticker.premarketOpen;
    const dbOpen = sessionOpens[comp.symbol];
    if (dbOpen && dbOpen.premarket_open > 0) return dbOpen.premarket_open;
    return 0;
  }, [sessionOpens]);

  // Pre-market score, sector breakdown, and full ticker list
  const pmResult = useMemo(() => {
    let rawScore = 0;
    let totalWeight = 0;
    let matchedCount = 0;
    const sectorMap = {};
    const allTickers = [];

    for (const comp of components) {
      const t = tickers[comp.symbol];
      const pmOpen = getPremarketOpen(t, comp);
      const hasTrade = pmOpen > 0 && t && t.price > 0;

      let changePercent = null;
      let contribution = null;

      if (hasTrade) {
        changePercent = ((t.price - pmOpen) / pmOpen) * 100;
        contribution = changePercent * comp.weight * 100;
        rawScore += contribution;
        totalWeight += comp.weight;
        matchedCount++;
      }

      allTickers.push({
        symbol: comp.symbol,
        company: comp.company_name,
        sector: comp.sector,
        weight: comp.weight,
        changePercent,
        contribution,
        price: t ? t.price : 0,
        hasTrade,
      });

      if (!sectorMap[comp.sector]) {
        sectorMap[comp.sector] = { sector: comp.sector, posSum: 0, negSum: 0, count: 0, matched: 0, up: 0, down: 0, neutral: 0, noTrade: 0, totalWeight: 0 };
      }
      sectorMap[comp.sector].count++;
      sectorMap[comp.sector].totalWeight += comp.weight * 100;
      if (hasTrade) {
        if (contribution > 0) sectorMap[comp.sector].posSum += contribution;
        else if (contribution < 0) sectorMap[comp.sector].negSum += Math.abs(contribution);
        sectorMap[comp.sector].matched++;
        if (changePercent > 0) sectorMap[comp.sector].up++;
        else if (changePercent < 0) sectorMap[comp.sector].down++;
        else sectorMap[comp.sector].neutral++;
      } else {
        sectorMap[comp.sector].noTrade++;
      }
    }

    const normalizedScore = totalWeight > 0
      ? Math.max(-100, Math.min(100, rawScore / totalWeight))
      : 0;

    const sectors = Object.values(sectorMap)
      .sort((a, b) => (b.posSum + b.negSum) - (a.posSum + a.negSum));

    // Weight tier breakdown
    const tiers = [
      { label: 'Mega Cap', min: 1.0, color: '#8b5cf6' },
      { label: 'Large Cap', min: 0.5, color: '#3b82f6' },
      { label: 'Mid Cap', min: 0.2, color: '#06b6d4' },
      { label: 'Small Cap', min: 0.1, color: '#f59e0b' },
      { label: 'Micro Cap', min: 0, color: '#6b7280' },
    ];
    const weightTiers = tiers.map(t => ({ ...t, contribution: 0, count: 0, matched: 0, up: 0, down: 0, neutral: 0, noTrade: 0, totalWeight: 0 }));
    for (const tk of allTickers) {
      const pct = tk.weight * 100;
      const tier = weightTiers.find(t => pct >= t.min);
      if (tier) {
        tier.count++;
        tier.totalWeight += tk.weight * 100;
        if (tk.hasTrade) {
          tier.contribution += tk.contribution;
          tier.matched++;
          if (tk.changePercent > 0) tier.up++;
          else if (tk.changePercent < 0) tier.down++;
          else tier.neutral++;
        } else {
          tier.noTrade++;
        }
      }
    }

    return { score: normalizedScore, matchedCount, allTickers, sectorBreakdown: sectors, weightTiers };
  }, [components, tickers, getPremarketOpen]);

  const fiveMinData = scoreHistory.fiveMin || [];
  const hourData = scoreHistory.hour || [];
  const pmData = scoreHistory.premarket || [];

  const fiveMinScore = useMemo(() => {
    if (fiveMinData.length === 0) return { score: 0, matchedCount: 0 };
    const last = fiveMinData[fiveMinData.length - 1];
    return { score: last.five_min_score ?? 0, matchedCount: last.five_min_matched ?? 0 };
  }, [fiveMinData]);

  const hourScore = useMemo(() => {
    if (hourData.length === 0) return { score: 0, matchedCount: 0 };
    const last = hourData[hourData.length - 1];
    return { score: last.hour_score ?? 0, matchedCount: last.hour_matched ?? 0 };
  }, [hourData]);

  // Summary counts
  const summary = useMemo(() => {
    let up = 0, down = 0, neutral = 0, noTrade = 0;
    for (const t of pmResult.allTickers) {
      if (!t.hasTrade) { noTrade++; continue; }
      if (t.changePercent > 0) up++;
      else if (t.changePercent < 0) down++;
      else neutral++;
    }
    return { up, down, neutral, noTrade };
  }, [pmResult.allTickers]);

  const handleSort = (col) => {
    if (sortCol === col) {
      setSortDir(d => d === 'desc' ? 'asc' : 'desc');
    } else {
      setSortCol(col);
      setSortDir('desc');
    }
  };

  // Sorted tickers for the table
  const sortedTickers = useMemo(() => {
    const sorted = [...pmResult.allTickers];
    sorted.sort((a, b) => {
      if (sortCol === 'weight') {
        return sortDir === 'desc' ? b.weight - a.weight : a.weight - b.weight;
      }
      if (!a.hasTrade && !b.hasTrade) return 0;
      if (!a.hasTrade) return 1;
      if (!b.hasTrade) return -1;
      return sortDir === 'desc'
        ? b.changePercent - a.changePercent
        : a.changePercent - b.changePercent;
    });
    return sorted;
  }, [pmResult.allTickers, sortCol, sortDir]);

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h1 className="text-2xl font-bold text-gray-900 mb-6">S&P 500 Pre-Market</h1>
        <div className="text-gray-500">Loading components...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h1 className="text-2xl font-bold text-gray-900 mb-6">S&P 500 Pre-Market</h1>
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-700">{error}</div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">S&P 500 Pre-Market</h1>
          <p className="text-sm text-gray-500 mt-1">
            {pmResult.matchedCount} / {components.length} components with pre-market data
          </p>
        </div>
      </div>

      {/* SPY 0 DTE Options */}
      <Spy0dteWidget />

      {/* Row 1: Last 5 Minutes */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mb-5">
        <ScoreCard
          title="Weighted Direction Score - LAST 5 MINUTES"
          score={fiveMinScore.score}
          matchedCount={fiveMinScore.matchedCount}
        />
        <ScoreChart
          title="Score - Last 5 Minutes"
          data={fiveMinData}
          dataKey="five_min_score"
          gradientId="pm5minGrad"
        />
      </div>

      {/* Row 2: Last 1 Hour */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mb-5">
        <ScoreCard
          title="Weighted Direction Score - LAST 1 HOUR"
          score={hourScore.score}
          matchedCount={hourScore.matchedCount}
        />
        <ScoreChart
          title="Score - Last 1 Hour"
          data={hourData}
          dataKey="hour_score"
          gradientId="pmHourGrad"
        />
      </div>

      {/* Row 2: Pre-market */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mb-5">
        <ScoreCard
          title="Weighted Pre-Market Direction Score"
          score={pmResult.score}
          matchedCount={pmResult.matchedCount}
        />
        <ScoreChart
          title="Score - From Pre-Market Open"
          data={pmData}
          dataKey="premarket_score"
          gradientId="pmPreGrad"
        />
      </div>

      {/* Row 3: Sector Breakdown */}
      <div className="mb-5 bg-gray-900 rounded-2xl p-5">
        <p className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-4">
          Sector Breakdown - From Pre-Market Open
        </p>
        {pmResult.matchedCount === 0 ? (
          <div className="text-gray-500 text-sm text-center py-8">Waiting for pre-market data...</div>
        ) : (
          <div className="space-y-3">
            {(() => {
              let overallPos = 0, overallNeg = 0;
              let overallUp = 0, overallDown = 0, overallNeutral = 0, overallNoTrade = 0;
              let overallTW = 0;
              const sectorRows = pmResult.sectorBreakdown.map((s) => {
                const color = SECTOR_COLORS[s.sector] || '#6b7280';
                overallPos += s.posSum;
                overallNeg += s.negSum;
                overallUp += s.up;
                overallDown += s.down;
                overallNeutral += s.neutral;
                overallNoTrade += s.noTrade;
                overallTW += s.totalWeight;
                const totalMag = s.posSum + s.negSum || 1;
                const posPct = (s.posSum / totalMag) * 100;
                const negPct = (s.negSum / totalMag) * 100;
                const sectorImpact = s.posSum - s.negSum;
                return (
                  <div key={s.sector}>
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-2">
                        <span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: color }} />
                        <span className="text-sm text-gray-300">{s.sector}</span>
                        <span className="text-[10px] text-gray-500">{s.count}</span>
                        <span className="text-[10px] font-semibold text-green-400">{s.up}</span>
                        <span className="text-[10px] font-semibold text-red-400">{s.down}</span>
                        <span className="text-[10px] font-semibold text-gray-400">{s.neutral}</span>
                        <span className="text-[10px] font-semibold" style={{ color: '#fb923c' }}>{s.noTrade}</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-[10px] text-gray-400">TW <span className="font-semibold text-gray-300">{s.totalWeight.toFixed(1)}%</span></span>
                        <span className={`text-[10px] font-semibold ${sectorImpact >= 0 ? 'text-green-400' : 'text-red-400'}`}>TI {sectorImpact >= 0 ? '+' : ''}{sectorImpact.toFixed(1)}</span>
                        <span className="text-[10px] font-semibold text-green-400">+{s.posSum.toFixed(1)}</span>
                        <span className="text-[10px] font-semibold text-red-400">-{s.negSum.toFixed(1)}</span>
                      </div>
                    </div>
                    <div className="h-3 bg-gray-800 rounded-full overflow-hidden flex">
                      {posPct > 0 && <div style={{ width: `${posPct}%`, backgroundColor: '#22c55e' }} className="h-full transition-all duration-500" />}
                      {negPct > 0 && <div style={{ width: `${negPct}%`, backgroundColor: '#ef4444' }} className="h-full transition-all duration-500" />}
                    </div>
                  </div>
                );
              });
              const overallTotal = overallPos + overallNeg || 1;
              const overallPosPct = (overallPos / overallTotal) * 100;
              const overallNegPct = (overallNeg / overallTotal) * 100;
              const totalCount = overallUp + overallDown + overallNeutral + overallNoTrade;
              return (
                <>
                  {sectorRows}
                  <div className="border-t border-gray-700 pt-3 mt-1">
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-2">
                        <span className="inline-block w-2.5 h-2.5 rounded-sm bg-white" />
                        <span className="text-sm font-semibold text-gray-200">Overall</span>
                        <span className="text-[10px] text-gray-500">{totalCount}</span>
                        <span className="text-[10px] font-semibold text-green-400">{overallUp}</span>
                        <span className="text-[10px] font-semibold text-red-400">{overallDown}</span>
                        <span className="text-[10px] font-semibold text-gray-400">{overallNeutral}</span>
                        <span className="text-[10px] font-semibold" style={{ color: '#fb923c' }}>{overallNoTrade}</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-[10px] text-gray-400">TW <span className="font-semibold text-gray-300">{overallTW.toFixed(1)}%</span></span>
                        <span className={`text-[10px] font-semibold ${(overallPos - overallNeg) >= 0 ? 'text-green-400' : 'text-red-400'}`}>TI {(overallPos - overallNeg) >= 0 ? '+' : ''}{(overallPos - overallNeg).toFixed(1)}</span>
                        <span className="text-[10px] font-semibold text-green-400">+{overallPos.toFixed(1)}</span>
                        <span className="text-[10px] font-semibold text-red-400">-{overallNeg.toFixed(1)}</span>
                      </div>
                    </div>
                    <div className="h-6 bg-gray-800 rounded-full overflow-hidden flex">
                      {overallPosPct > 0 && (
                        <div style={{ width: `${overallPosPct}%`, backgroundColor: '#22c55e' }} className="h-full transition-all duration-500 flex items-center justify-center">
                          <span className="text-[10px] font-bold text-white">{overallPosPct.toFixed(1)}%</span>
                        </div>
                      )}
                      {overallNegPct > 0 && (
                        <div style={{ width: `${overallNegPct}%`, backgroundColor: '#ef4444' }} className="h-full transition-all duration-500 flex items-center justify-center">
                          <span className="text-[10px] font-bold text-white">{overallNegPct.toFixed(1)}%</span>
                        </div>
                      )}
                    </div>
                  </div>
                </>
              );
            })()}
          </div>
        )}
      </div>

      {/* Stacked Bar by contribution magnitude */}
      <div className="mb-5 bg-gray-900 rounded-2xl p-5">
        <p className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-4">
          Stacked Bar (by contribution magnitude)
        </p>
        {pmResult.matchedCount === 0 ? (
          <div className="text-gray-500 text-sm text-center py-8">Waiting for pre-market data...</div>
        ) : (
          <div className="space-y-3">
            {(() => {
              let overallPos = 0, overallNeg = 0;
              let overallUp = 0, overallDown = 0, overallNeutral = 0, overallNoTrade = 0;
              let overallTW = 0;
              const tierRows = pmResult.weightTiers.map((t) => {
                let posSum = 0;
                let negSum = 0;
                for (const tk of pmResult.allTickers) {
                  const pct = tk.weight * 100;
                  const tierMatch = pmResult.weightTiers.find(wt => pct >= wt.min);
                  if (tierMatch && tierMatch.label === t.label && tk.hasTrade) {
                    if (tk.contribution > 0) posSum += tk.contribution;
                    else if (tk.contribution < 0) negSum += Math.abs(tk.contribution);
                  }
                }
                overallPos += posSum;
                overallNeg += negSum;
                overallUp += t.up;
                overallDown += t.down;
                overallNeutral += t.neutral;
                overallNoTrade += t.noTrade;
                overallTW += t.totalWeight;
                const totalMag = posSum + negSum || 1;
                const posPct = (posSum / totalMag) * 100;
                const negPct = (negSum / totalMag) * 100;
                const tierImpact = posSum - negSum;
                return (
                  <div key={t.label}>
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-2">
                        <span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: t.color }} />
                        <span className="text-sm text-gray-300">{t.label}</span>
                        <span className="text-[10px] text-gray-500">{t.count}</span>
                        <span className="text-[10px] font-semibold text-green-400">{t.up}</span>
                        <span className="text-[10px] font-semibold text-red-400">{t.down}</span>
                        <span className="text-[10px] font-semibold text-gray-400">{t.neutral}</span>
                        <span className="text-[10px] font-semibold" style={{ color: '#fb923c' }}>{t.noTrade}</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-[10px] text-gray-400">TW <span className="font-semibold text-gray-300">{t.totalWeight.toFixed(1)}%</span></span>
                        <span className={`text-[10px] font-semibold ${tierImpact >= 0 ? 'text-green-400' : 'text-red-400'}`}>TI {tierImpact >= 0 ? '+' : ''}{tierImpact.toFixed(1)}</span>
                        <span className="text-[10px] font-semibold text-green-400">+{posSum.toFixed(1)}</span>
                        <span className="text-[10px] font-semibold text-red-400">-{negSum.toFixed(1)}</span>
                      </div>
                    </div>
                    <div className="h-3 bg-gray-800 rounded-full overflow-hidden flex">
                      {posPct > 0 && <div style={{ width: `${posPct}%`, backgroundColor: '#22c55e' }} className="h-full transition-all duration-500" />}
                      {negPct > 0 && <div style={{ width: `${negPct}%`, backgroundColor: '#ef4444' }} className="h-full transition-all duration-500" />}
                    </div>
                  </div>
                );
              });
              const overallTotal = overallPos + overallNeg || 1;
              const overallPosPct = (overallPos / overallTotal) * 100;
              const overallNegPct = (overallNeg / overallTotal) * 100;
              const totalCount = overallUp + overallDown + overallNeutral + overallNoTrade;
              return (
                <>
                  {tierRows}
                  <div className="border-t border-gray-700 pt-3 mt-1">
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-2">
                        <span className="inline-block w-2.5 h-2.5 rounded-sm bg-white" />
                        <span className="text-sm font-semibold text-gray-200">Overall</span>
                        <span className="text-[10px] text-gray-500">{totalCount}</span>
                        <span className="text-[10px] font-semibold text-green-400">{overallUp}</span>
                        <span className="text-[10px] font-semibold text-red-400">{overallDown}</span>
                        <span className="text-[10px] font-semibold text-gray-400">{overallNeutral}</span>
                        <span className="text-[10px] font-semibold" style={{ color: '#fb923c' }}>{overallNoTrade}</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-[10px] text-gray-400">TW <span className="font-semibold text-gray-300">{overallTW.toFixed(1)}%</span></span>
                        <span className={`text-[10px] font-semibold ${(overallPos - overallNeg) >= 0 ? 'text-green-400' : 'text-red-400'}`}>TI {(overallPos - overallNeg) >= 0 ? '+' : ''}{(overallPos - overallNeg).toFixed(1)}</span>
                        <span className="text-[10px] font-semibold text-green-400">+{overallPos.toFixed(1)}</span>
                        <span className="text-[10px] font-semibold text-red-400">-{overallNeg.toFixed(1)}</span>
                      </div>
                    </div>
                    <div className="h-6 bg-gray-800 rounded-full overflow-hidden flex">
                      {overallPosPct > 0 && (
                        <div style={{ width: `${overallPosPct}%`, backgroundColor: '#22c55e' }} className="h-full transition-all duration-500 flex items-center justify-center">
                          <span className="text-[10px] font-bold text-white">{overallPosPct.toFixed(1)}%</span>
                        </div>
                      )}
                      {overallNegPct > 0 && (
                        <div style={{ width: `${overallNegPct}%`, backgroundColor: '#ef4444' }} className="h-full transition-all duration-500 flex items-center justify-center">
                          <span className="text-[10px] font-bold text-white">{overallNegPct.toFixed(1)}%</span>
                        </div>
                      )}
                    </div>
                  </div>
                </>
              );
            })()}
          </div>
        )}
      </div>

      {/* Full Ticker Table */}
      <div className="bg-gray-900 rounded-2xl p-5">
        <p className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-4">
          All S&P 500 Components - From Pre-Market Open
        </p>

        {/* Summary bar */}
        <div className="flex items-center gap-5 mb-4 text-sm">
          <div className="flex items-center gap-1.5">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="#4ade80" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 15l7-7 7 7" />
            </svg>
            <span className="font-semibold tabular-nums" style={{ color: '#4ade80' }}>{summary.up}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="#f87171" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
            </svg>
            <span className="font-semibold tabular-nums" style={{ color: '#f87171' }}>{summary.down}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="#9ca3af" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 12h14" />
            </svg>
            <span className="font-semibold tabular-nums" style={{ color: '#9ca3af' }}>{summary.neutral}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="#fb923c" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
            </svg>
            <span className="font-semibold tabular-nums" style={{ color: '#fb923c' }}>{summary.noTrade}</span>
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto max-h-[600px] overflow-y-auto pr-2">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-gray-900 z-10">
              <tr className="text-left text-[10px] font-medium text-gray-500 uppercase tracking-wider">
                <th className="pb-2 pt-1">Symbol</th>
                <th
                  className="pb-2 pt-1 text-right cursor-pointer select-none hover:text-gray-300 transition-colors"
                  onClick={() => handleSort('weight')}
                >
                  Weight {sortCol === 'weight' ? (sortDir === 'desc' ? '\u25BC' : '\u25B2') : ''}
                </th>
                <th
                  className="pb-2 pt-1 text-right cursor-pointer select-none hover:text-gray-300 transition-colors"
                  onClick={() => handleSort('change')}
                >
                  Change % {sortCol === 'change' ? (sortDir === 'desc' ? '\u25BC' : '\u25B2') : ''}
                </th>
                <th className="pb-2 pt-1 text-right">Impact</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-800">
              {sortedTickers.map((c) => {
                if (!c.hasTrade) {
                  return (
                    <tr
                      key={c.symbol}
                      className="hover:bg-gray-800/50 cursor-pointer transition-colors opacity-40"
                      onClick={() => window.open(`https://www.tradingview.com/chart/?symbol=${c.symbol}&interval=1`, '_blank', 'noopener')}
                    >
                      <td className="py-1.5">
                        <span className="font-semibold text-gray-200">{c.symbol}</span>
                      </td>
                      <td className="py-1.5 text-right text-gray-400 tabular-nums">
                        {(c.weight * 100).toFixed(2)}%
                      </td>
                      <td className="py-1.5 text-right text-gray-500">{'\u2014'}</td>
                      <td className="py-1.5 text-right text-gray-500">{'\u2014'}</td>
                    </tr>
                  );
                }
                const isPositive = c.contribution >= 0;
                return (
                  <tr
                    key={c.symbol}
                    className="hover:bg-gray-800/50 cursor-pointer transition-colors"
                    onClick={() => window.open(`https://www.tradingview.com/chart/?symbol=${c.symbol}&interval=1`, '_blank', 'noopener')}
                  >
                    <td className="py-1.5">
                      <div className="flex items-center gap-2">
                        <span
                          className="inline-block w-1 h-4 rounded-full"
                          style={{ backgroundColor: isPositive ? '#22c55e' : '#ef4444' }}
                        />
                        <span className="font-semibold text-gray-200">{c.symbol}</span>
                      </div>
                    </td>
                    <td className="py-1.5 text-right text-gray-400 tabular-nums">
                      {(c.weight * 100).toFixed(2)}%
                    </td>
                    <td className="py-1.5 text-right tabular-nums">
                      <span className={isPositive ? 'text-green-400' : 'text-red-400'}>
                        {c.changePercent >= 0 ? '+' : ''}{c.changePercent.toFixed(2)}%
                      </span>
                    </td>
                    <td className="py-1.5 text-right tabular-nums">
                      <span className={`font-semibold ${isPositive ? 'text-green-400' : 'text-red-400'}`}>
                        {isPositive ? '+' : ''}{c.contribution.toFixed(3)}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Visualization Widgets */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-6">

        {/* Widget 1: Traffic Light Gauge */}
        <div className="bg-gray-900 rounded-2xl p-5">
          <p className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-3">Traffic Light Gauge</p>
          <div className="flex flex-col items-center">
            <svg viewBox="0 0 200 120" className="w-full max-w-[200px]">
              <path d="M 20 100 A 80 80 0 0 0 63.7 28.7" fill="none" stroke="#ef4444" strokeWidth="12" strokeLinecap="round" />
              <path d="M 63.7 28.7 A 80 80 0 0 0 136.3 28.7" fill="none" stroke="#eab308" strokeWidth="12" strokeLinecap="round" />
              <path d="M 136.3 28.7 A 80 80 0 0 0 180 100" fill="none" stroke="#22c55e" strokeWidth="12" strokeLinecap="round" />
              {(() => {
                const angle = (180 - ((pmResult.score + 100) / 200) * 180) * Math.PI / 180;
                const nx = 100 + 65 * Math.cos(angle);
                const ny = 100 - 65 * Math.sin(angle);
                return <line x1="100" y1="100" x2={nx} y2={ny} stroke="white" strokeWidth="3" strokeLinecap="round" />;
              })()}
              <circle cx="100" cy="100" r="5" fill="white" />
            </svg>
            <div className="text-2xl font-bold mt-2" style={{ color: getScoreColor(pmResult.score) }}>
              {pmResult.matchedCount > 0 ? (pmResult.score >= 0 ? '+' : '') + pmResult.score.toFixed(1) : '\u2014'}
            </div>
            <div className="text-xs text-gray-400 mt-1">
              {pmResult.matchedCount > 0 ? getScoreLabel(pmResult.score) : 'Waiting for data'}
            </div>
          </div>
        </div>

        {/* Widget 2: Bull/Bear Tug-of-War */}
        <div className="bg-gray-900 rounded-2xl p-5">
          <p className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-3">Bull / Bear Tug-of-War</p>
          {(() => {
            const countTotal = summary.up + summary.down || 1;
            const bullPct = (summary.up / countTotal) * 100;
            const bearPct = (summary.down / countTotal) * 100;
            let contribUp = 0, contribDown = 0;
            for (const t of pmResult.allTickers) {
              if (t.hasTrade && t.contribution > 0) contribUp += t.contribution;
              else if (t.hasTrade && t.contribution < 0) contribDown += Math.abs(t.contribution);
            }
            const contribTotal = contribUp + contribDown || 1;
            const wBullPct = (contribUp / contribTotal) * 100;
            const wBearPct = (contribDown / contribTotal) * 100;
            return (
              <div className="space-y-4">
                <div>
                  <div className="flex justify-between text-[10px] text-gray-400 mb-1">
                    <span>Count: {summary.up} up</span>
                    <span>{summary.down} down</span>
                  </div>
                  <div className="relative h-6 bg-gray-800 rounded-full overflow-hidden flex">
                    {bullPct > 0 && <div style={{ width: `${bullPct}%`, backgroundColor: '#22c55e' }} className="h-full transition-all duration-500" />}
                    {bearPct > 0 && <div style={{ width: `${bearPct}%`, backgroundColor: '#ef4444' }} className="h-full transition-all duration-500" />}
                    <div className="absolute inset-0 flex items-center justify-center">
                      <span className="text-[10px] font-bold text-white drop-shadow">{bullPct.toFixed(0)}% / {bearPct.toFixed(0)}%</span>
                    </div>
                  </div>
                </div>
                <div>
                  <div className="flex justify-between text-[10px] text-gray-400 mb-1">
                    <span>Contribution: +{contribUp.toFixed(1)}</span>
                    <span>-{contribDown.toFixed(1)}</span>
                  </div>
                  <div className="relative h-6 bg-gray-800 rounded-full overflow-hidden flex">
                    {wBullPct > 0 && <div style={{ width: `${wBullPct}%`, backgroundColor: '#22c55e' }} className="h-full transition-all duration-500" />}
                    {wBearPct > 0 && <div style={{ width: `${wBearPct}%`, backgroundColor: '#ef4444' }} className="h-full transition-all duration-500" />}
                    <div className="absolute inset-0 flex items-center justify-center">
                      <span className="text-[10px] font-bold text-white drop-shadow">{wBullPct.toFixed(0)}% / {wBearPct.toFixed(0)}%</span>
                    </div>
                  </div>
                </div>
                <div className="text-center">
                  <span className="text-lg font-bold" style={{ color: getScoreColor(pmResult.score) }}>
                    Net: {pmResult.score >= 0 ? '+' : ''}{pmResult.score.toFixed(1)}
                  </span>
                </div>
              </div>
            );
          })()}
        </div>

        {/* Widget 3: Weighted Breadth Dial */}
        <div className="bg-gray-900 rounded-2xl p-5">
          <p className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-3">Weighted Breadth Dial</p>
          {(() => {
            const traded = summary.up + summary.down + summary.neutral || 1;
            const countBreadth = summary.up / traded;
            let posWeight = 0, totalTradedWeight = 0;
            for (const t of pmResult.allTickers) {
              if (t.hasTrade) {
                totalTradedWeight += t.weight;
                if (t.contribution > 0) posWeight += t.weight;
              }
            }
            const weightBreadth = totalTradedWeight > 0 ? posWeight / totalTradedWeight : 0;
            const outerR = 50, innerR = 35;
            const outerC = 2 * Math.PI * outerR;
            const innerC = 2 * Math.PI * innerR;
            return (
              <div className="flex flex-col items-center">
                <svg viewBox="0 0 120 120" className="w-full max-w-[160px]">
                  <circle cx="60" cy="60" r={outerR} fill="none" stroke="#374151" strokeWidth="10" />
                  <circle cx="60" cy="60" r={outerR} fill="none" stroke="#22c55e" strokeWidth="10"
                    strokeDasharray={`${countBreadth * outerC} ${outerC}`}
                    transform="rotate(-90 60 60)"
                    className="transition-all duration-500" />
                  <circle cx="60" cy="60" r={innerR} fill="none" stroke="#374151" strokeWidth="10" />
                  <circle cx="60" cy="60" r={innerR} fill="none" stroke="#3b82f6" strokeWidth="10"
                    strokeDasharray={`${weightBreadth * innerC} ${innerC}`}
                    transform="rotate(-90 60 60)"
                    className="transition-all duration-500" />
                  <text x="60" y="56" textAnchor="middle" fill="white" fontSize="14" fontWeight="bold">
                    {(countBreadth * 100).toFixed(0)}%
                  </text>
                  <text x="60" y="70" textAnchor="middle" fill="#9ca3af" fontSize="9">
                    breadth
                  </text>
                </svg>
                <div className="flex gap-4 mt-2 text-[10px]">
                  <div className="flex items-center gap-1">
                    <span className="inline-block w-2 h-2 rounded-full" style={{ backgroundColor: '#22c55e' }} />
                    <span className="text-gray-400">Count {(countBreadth * 100).toFixed(0)}%</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <span className="inline-block w-2 h-2 rounded-full" style={{ backgroundColor: '#3b82f6' }} />
                    <span className="text-gray-400">Weight {(weightBreadth * 100).toFixed(0)}%</span>
                  </div>
                </div>
              </div>
            );
          })()}
        </div>

        {/* Widget 4: Real-time Pulse Line */}
        <div className="bg-gray-900 rounded-2xl p-5">
          <p className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-3">Pre-Market Pulse</p>
          {pmData.length < 2 ? (
            <div className="h-32 flex items-center justify-center text-gray-500 text-sm">Collecting data...</div>
          ) : (
            <div className="h-32">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={pmData}>
                  <defs>
                    <linearGradient id="pulseGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={pmResult.score >= 0 ? '#22c55e' : '#ef4444'} stopOpacity={0.3} />
                      <stop offset="100%" stopColor={pmResult.score >= 0 ? '#22c55e' : '#ef4444'} stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <XAxis dataKey="time" tick={{ fontSize: 9, fill: '#6b7280' }} interval="preserveStartEnd" />
                  <YAxis tick={{ fontSize: 9, fill: '#6b7280' }} domain={['auto', 'auto']} />
                  <Tooltip
                    contentStyle={{ backgroundColor: '#1f2937', border: '1px solid #374151', borderRadius: '8px', fontSize: '11px' }}
                    labelStyle={{ color: '#9ca3af' }}
                    formatter={(v) => [v != null ? v.toFixed(2) : '\u2014', 'Score']}
                  />
                  <Area type="monotone" dataKey="premarket_score" stroke={pmResult.score >= 0 ? '#22c55e' : '#ef4444'} fill="url(#pulseGrad)" strokeWidth={2} dot={false} connectNulls />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>

        {/* Widget 5: Sector Impact Strip */}
        <div className="lg:col-span-2 bg-gray-900 rounded-2xl p-5">
          <p className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-3">Sector Impact Strip</p>
          {pmResult.matchedCount === 0 ? (
            <div className="text-gray-500 text-sm text-center py-8">Waiting for data...</div>
          ) : (
            <div className="space-y-2">
              {(() => {
                const sectors = pmResult.sectorBreakdown.map(s => ({
                  ...s,
                  netContribution: s.posSum - s.negSum,
                }));
                sectors.sort((a, b) => Math.abs(b.netContribution) - Math.abs(a.netContribution));
                const maxAbs = Math.max(...sectors.map(s => Math.abs(s.netContribution)), 0.01);
                const sectorWeightMap = {};
                for (const t of pmResult.allTickers) {
                  if (!sectorWeightMap[t.sector]) sectorWeightMap[t.sector] = 0;
                  sectorWeightMap[t.sector] += t.weight;
                }
                return sectors.map(s => {
                  const isPos = s.netContribution >= 0;
                  const barWidth = (Math.abs(s.netContribution) / maxAbs) * 100;
                  const sectorColor = SECTOR_COLORS[s.sector] || '#6b7280';
                  const totalWeight = sectorWeightMap[s.sector] || 0;
                  return (
                    <div key={s.sector} className="flex items-center gap-2">
                      <span className="inline-block w-1 h-5 rounded-full flex-shrink-0" style={{ backgroundColor: sectorColor }} />
                      <span className="text-[11px] text-gray-300 w-32 truncate flex-shrink-0">{s.sector}</span>
                      <div className="flex-1 h-4 bg-gray-800 rounded overflow-hidden">
                        <div
                          className="h-full rounded transition-all duration-500"
                          style={{ width: `${Math.max(barWidth, 1)}%`, backgroundColor: isPos ? '#22c55e' : '#ef4444' }}
                        />
                      </div>
                      <span className={`text-[10px] font-semibold w-12 text-right flex-shrink-0 ${isPos ? 'text-green-400' : 'text-red-400'}`}>
                        {isPos ? '+' : ''}{s.netContribution.toFixed(1)}
                      </span>
                      <span className="text-[10px] text-gray-500 w-10 text-right flex-shrink-0">
                        {(totalWeight * 100).toFixed(1)}%
                      </span>
                    </div>
                  );
                });
              })()}
            </div>
          )}
        </div>

      </div>
    </div>
  );
}
